import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { tryGetCryptoCurrencyListByUser } from "../../services/crypto-currency/cripto-currency.service";
import { ICryptoCurrency } from "../../interfaces/crypto-currency/ICryptoCurrency";
import { formatPriceToDolar } from "../../common/utils/number";
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../loader";
import { useNotification } from "../notification/useNotification";
import extractErrorMessage from "../../common/utils/errorMessages";

export default function CryptoPriceSlider() {
    const [coins, setCoins] = useState<ICryptoCurrency[]>([]);
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();


    const fetchCoins = async () => {
        try {
            const response = await tryGetCryptoCurrencyListByUser();

            if (response.status === 200) {
                setCoins(response.data);
            }

        } catch (error) {
            displayNotification({ type:'error', message: extractErrorMessage(error) });
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchCoins();
        setLoading(false);
    }, []);

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 10,
        autoplay: true,
        autoplaySpeed: 3000,
        responsive: [
            { breakpoint: 1200, settings: { slidesToShow: 10 } },
            { breakpoint: 960, settings: { slidesToShow: 8 } },
            { breakpoint: 768, settings: { slidesToShow: 5 } },
            { breakpoint: 480, settings: { slidesToShow: 4 } },
        ],
    };

    return (
        <>
            {showLoading ? <Loader /> : false}
            <Slider {...settings}>
                {coins.map((coin) => (
                    <Card
                        key={coin.id}
                        sx={{
                            width: { xs: 70, sm: 85, md: 100 },
                            height: { xs: 110, sm: 110, md: 120 },
                            m: 0.75,
                            borderRadius: 2,
                            border: '1px solid rgba(255,215,0,0.12)',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'border-color 0.2s',
                            '&:hover': {
                                borderColor: 'primary.main',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                width: '100%',
                                height: '100%',
                                px: 1,
                            }}
                        >
                            <Box
                                component="img"
                                src={coin.imageUrl}
                                alt={coin.name}
                                sx={{ height: 24, mb: 0.75 }}
                            />

                            <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ whiteSpace: 'normal', wordWrap: 'break-word', color: 'text.primary', lineHeight: 1.3 }}
                            >
                                {coin.name}
                            </Typography>

                            <Typography variant="caption" sx={{ mt: 0.5, color: 'secondary.main', fontWeight: 600 }}>
                                {formatPriceToDolar(coin.price)}
                            </Typography>

                            <Typography
                                variant="caption"
                                sx={{
                                    mt: 0.25,
                                    color: coin.priceChangePercentage24h >= 0 ? 'success.main' : 'error.main',
                                    fontWeight: 500,
                                }}
                            >
                                {coin.priceChangePercentage24h.toFixed(2)}%
                            </Typography>
                        </Box>
                    </Card>
                ))}
            </Slider>
        </>
    );
}